/**
 * Draggable candidate item component
 */

import React, { useRef, useState } from 'react'
import { colors } from '@fs/zion-ui'
import { css } from '@emotion/core'
import type { Candidate } from '../../types'

interface CandidateItemProps {
  candidate: Candidate
  dragIndex: number
  isFirst: boolean
  nativeDragApi: {
    onDragStart: (e: React.DragEvent<HTMLDivElement>) => void
    onDragOver: (e: React.DragEvent<HTMLDivElement>) => void
    onDragEnter: (e: React.DragEvent<HTMLDivElement>) => void
    onDrop: (e: React.DragEvent<HTMLDivElement>) => void
  }
  onTouchDragStart?: (dragIndex: number) => void
  onTouchDragOver?: (clientY: number) => void
  onTouchDragEnd?: () => void
}

const containerCss = (isFirst: boolean, isDragging: boolean): ReturnType<typeof css> => css`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  margin: 3px 0;
  background-color: ${isFirst ? colors.green03 : colors.gray02};
  border: 1px solid ${isFirst ? colors.green20 : colors.gray20};
  border-radius: 6px;
  cursor: grab;
  transition: all 0.2s ease;
  user-select: none;
  touch-action: none;
  ${isDragging &&
  `
    opacity: 0.7;
    transform: scale(1.02);
    box-shadow: 0 6px 16px ${colors.transparent.gray40};
    cursor: grabbing;
    z-index: 1000;
  `}

  &:hover {
    background-color: ${isFirst ? colors.green05 : colors.gray05};
    box-shadow: 0 2px 6px ${colors.transparent.gray30};
    transform: translateY(-1px);
  }

  &:active {
    cursor: grabbing;
    opacity: 0.7;
    transform: translateY(0);
    box-shadow: 0 4px 12px ${colors.transparent.gray40};
  }

  &:focus {
    outline: 2px solid ${colors.blue50};
    outline-offset: 2px;
  }
`

const dragHandleCss = css`
  color: ${colors.gray40};
  font-size: 18px;
  margin-inline-end: 10px;
  cursor: grab;
  line-height: 1;
  user-select: none;

  &:hover {
    color: ${colors.gray60};
  }
`

const rankBadgeCss = (isFirst: boolean): ReturnType<typeof css> => css`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${isFirst ? colors.green60 : colors.blue60};
  color: ${colors.gray00};
  font-weight: 700;
  font-size: 16px;
  margin-inline-end: 12px;
  flex-shrink: 0;
`

const candidateTextCss = css`
  font-size: 18px;
  line-height: 1.4;
  flex: 1;
  word-break: break-word;
`

const changeIndicatorCss = css`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: ${colors.gray50};
  margin-inline-start: auto;
  padding-inline-start: 12px;
  font-style: italic;
  white-space: nowrap;
`

const arrowCss = (direction: 'up' | 'down'): ReturnType<typeof css> => css`
  font-size: 14px;
  color: ${direction === 'up' ? colors.green50 : colors.gray50};
`

export const CandidateItem: React.FC<CandidateItemProps> = ({
  candidate,
  dragIndex,
  isFirst,
  nativeDragApi,
  onTouchDragStart,
  onTouchDragOver,
  onTouchDragEnd,
}) => {
  // Current visual position is dragIndex + 1
  const currentPosition = dragIndex + 1
  const hasRankChanged = currentPosition !== candidate.originalRank
  const [isDragging, setIsDragging] = useState(false)
  const elementRef = useRef<HTMLDivElement>(null)

  // Determine rank direction (simplified to avoid nested ternary)
  let rankDirection: 'up' | 'down' | null = null
  if (hasRankChanged) {
    rankDirection = currentPosition < candidate.originalRank ? 'up' : 'down'
  }

  // Touch event handlers for mobile drag-and-drop
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>): void => {
    setIsDragging(true)
    onTouchDragStart?.(dragIndex)
    // Prevent default to avoid scrolling while dragging
    e.preventDefault()
  }

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>): void => {
    if (!isDragging) return
    const touch = e.touches[0]
    onTouchDragOver?.(touch.clientY)
    e.preventDefault()
  }

  const handleTouchEnd = (): void => {
    setIsDragging(false)
    onTouchDragEnd?.()
  }

  return (
    <div
      ref={elementRef}
      css={containerCss(isFirst, isDragging)}
      draggable
      data-drag-index={dragIndex}
      tabIndex={0}
      {...nativeDragApi}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      role="button"
      aria-label={`Rank ${currentPosition}: ${candidate.text}${
        hasRankChanged ? `, moved ${rankDirection} from rank ${candidate.originalRank}` : ''
      }`}
    >
      <div css={dragHandleCss} aria-hidden="true">
        ⋮⋮
      </div>

      <div css={rankBadgeCss(isFirst)} aria-label={`Rank ${currentPosition}`}>
        {currentPosition}
      </div>

      <div css={candidateTextCss}>{candidate.text}</div>

      {hasRankChanged && rankDirection && (
        <div css={changeIndicatorCss}>
          <span css={arrowCss(rankDirection)}>{rankDirection === 'up' ? '↑' : '↓'}</span>
          <span>was {candidate.originalRank}</span>
        </div>
      )}
    </div>
  )
}
